<template>

  <v-layout column>
    <v-card id="device" class="intro-dark mx-auto mb-5" width="100%" max-width="480">
      <v-card-title class="py-4 align-center justify-center">
        <mrx-translation :token="`app.device.title`"></mrx-translation>
      </v-card-title>

      <!-- Tab Navigation -->
      <div class="device-tabs-wrapper">
        <div class="device-tabs">
          <button
            id="device-tab-status"
            class="device-tab"
            :class="{ 'device-tab--active': activeTab === 'status' }"
            @click="activeTab = 'status'"
          >
            <mrx-translation token="app.device.tabs.status"></mrx-translation>
          </button>
          <button
            id="device-tab-logs"
            class="device-tab"
            :class="{ 'device-tab--active': activeTab === 'logs' }"
            @click="activeTab = 'logs'"
          >
            <mrx-translation token="app.device.tabs.logs"></mrx-translation>
          </button>
        </div>
      </div>

      <v-card-text class="px-0 device-card-content">

        <!-- STATUS Tab Content -->
        <div v-show="activeTab === 'status'" class="status-tab-wrapper">
          <v-list two-line class="intro-dark pa-0">
            <v-list-item>
              <v-list-item-action>
                <v-badge
                  :color="badgeColor(this.connection)"
                  overlap
                  size="12"
                >
                  <v-avatar size="54" color="primaryblue"><v-img max-height="30" contain :src="connectionStatusIcon"></v-img></v-avatar>
                </v-badge>              
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title><mrx-translation token="app.device.list.connection_status.title"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle><mrx-translation :token="connectionStatusText"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation :prepend="$n(mbps)" token="app.device.list.connection_speed.title"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation :prepend="estimatedTime" token="app.device.list.connection_time.title"></mrx-translation></v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-action>
                <v-badge
                :color="badgeColor(this.records === 0)"
                overlap
                size="12"
                >
                  <v-avatar size="54" color="primaryblue"><v-icon size="24">{{icons.mdiCloudUpload}}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.records_in_queue.title`"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle><mrx-translation :prepend="records.toString()" :token="`app.device.list.records_in_queue.awaiting`"></mrx-translation></v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="(!syncActive && records > 0 && connection) || showUploadButton">
              <v-list-item-action>
              </v-list-item-action>
              <v-list-item-content>
                <v-btn 
                  id="sync-encounters"
                  color="orange"
                  @click="syncQueuedEncounters()"
                >
                  <v-icon left>{{icons.mdiCloudUpload}}</v-icon>
                  Upload Records
                </v-btn>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-action>
                <v-badge
                  :color="badgeColor(geoPermissionStatus === 'granted')"
                  overlap
                  size="12"
                >
                  <v-avatar size="54" color="primaryblue"><v-icon size="24">{{icons.mdiMapMarker}}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`questions.geo_permission.title`"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle><mrx-translation :token="geoPermissionText"></mrx-translation></v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-action>
                <v-badge
                  :color="badgeColor(geoAcquired)"
                  overlap
                  size="12"
                >
                  <v-avatar size="54" color="primaryblue"><v-icon size="24">{{ icons.mdiCrosshairsGps }}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.location_check.title`"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle><mrx-translation :token="geoLocationCheckText"></mrx-translation></v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-action>
                <v-badge
                  :color="badgeColor(versionCheck)"
                  overlap
                  size="12"
                >
                  <v-avatar size="54" color="primaryblue"><v-icon size="24">{{icons.mdiUpdate}}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.version_check.title`"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle><mrx-translation :prepend="$store.state.release.version" :token="versionCheckText"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation :token="`app.device.list.version.hrid.title`"></mrx-translation> {{$store.state.v4.registration.hrId}}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item>
              <v-list-item-action>
                <v-badge
                  :color="badgeColor(offlineCheck)"
                  overlap
                  size="12"
                >
                  <v-avatar size="54" color="primaryblue"><v-icon size="24">{{icons.mdiLightningBoltCircle}}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.offline.title`"></mrx-translation></v-list-item-title>
                <v-container class="pt-2 pl-0" v-if="this.getMode('show')">
                  <v-row no-gutters>
                    <v-btn
                    :disabled = "!this.online"
                    id="refresh-cache"
                    @click="cacheDialog=true"
                    outlined
                    class="reset-warning"
                    >
                      <mrx-translation :token="cacheRefreshText"></mrx-translation>
                    </v-btn>
                  </v-row>
                </v-container>
              </v-list-item-content>    
            </v-list-item>

            <v-list-item>
              <v-list-item-action>
                  <v-badge
                  :color="queueCheckIcon"
                  overlap
                  size="12"
                >
                  <v-avatar size="54" color="primaryblue"><v-icon size="24">{{icons.mdiCogTransfer}}</v-icon></v-avatar>
                </v-badge>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title><mrx-translation :token="`app.device.list.queue.title`"></mrx-translation></v-list-item-title>
                <v-list-item-subtitle><mrx-translation :append="$d(new Date(this.queueStartedAt))" :token="`app.device.list.queue.data_since.title`"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation :prepend="totalCompletedTracks.toString()"  :token="`app.device.list.queue.total_tracks.title`"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation :prepend="records.toString()" :token="`app.device.list.records_in_queue.title`"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation token="app.device.list.queue.elapsed.title"></mrx-translation></v-list-item-subtitle>
                <v-list-item-subtitle><mrx-translation :prepend="timeSinceLastUpload.count.toString()" :token="`partials.time.${timeSinceLastUpload.label}`"></mrx-translation></v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-card-actions >
            <v-container>
              <v-row no-gutters class="justify-center align-center">
                <v-btn
                  id="open-reset-statistics-dialog"
                  v-if="records === 0"
                  @click="resetDialog=true"
                  outlined
                  class="reset-warning"
                >
                  <mrx-translation token="app.device.list.queue.reset.button.title"></mrx-translation>
                </v-btn>
              </v-row>
              <v-row no-gutters class="justify-center align-center pt-4">
                <v-btn
                  id="device-return-to-previous"
                  @click="previous()"
                >
                  <mrx-translation token="app.form.return_to_assessment"></mrx-translation>
                </v-btn>
              </v-row>
            </v-container>
          </v-card-actions>
        </div>

        <!-- LOGS Tab Content -->
        <div v-show="activeTab === 'logs'" class="logs-tab-wrapper">
          <diagnostic-log-viewer class="logs-viewer-flex"></diagnostic-log-viewer>

          <v-card-actions class="mt-auto">
            <v-container>
              <v-row no-gutters class="justify-center align-center pt-2">
                <v-btn
                  id="logs-return-to-previous"
                  @click="previous()"
                >
                  <mrx-translation token="app.form.return_to_assessment"></mrx-translation>
                </v-btn>
              </v-row>
            </v-container>
          </v-card-actions>
        </div>

        <v-dialog
          v-if="records === 0"
          overlay-color=var(--primary-blue)
          overlay-opacity="0.9"
          v-model="resetDialog"
          persistent
          max-width="290"
        >        
          <v-card class="intro-dark">
            <v-card-title>
              <mrx-translation token="app.device.list.queue.reset.dialog.title"></mrx-translation>
            </v-card-title>
            <v-card-text class="modal-text">
              <mrx-translation token="app.device.list.queue.reset.dialog.message"></mrx-translation>
            </v-card-text>
            <v-card-actions class="px-5 pb-5 pt-0 justify-stretch align-stretch">
              <v-spacer></v-spacer>
              <v-row no-gutters class="flex xs6 pe-2">
                <v-btn 
                  id="cancel-reset-statistics" 
                  class="cancel-btn" 
                  outlined 
                  block 
                  depressed 
                  @click="resetDialog = false">
                  <mrx-translation token="app.device.list.queue.reset.dialog.cancel"></mrx-translation>
                </v-btn>
              </v-row>
              <v-row no-gutters class="flex xs6 ps-2">
                <v-btn 
                  id="confirm-reset-statistics" 
                  class="modal-btn" 
                  block 
                  depressed 
                  @click="resetQueueData()">
                  <mrx-translation token="app.device.list.queue.reset.dialog.ok"></mrx-translation>
                </v-btn>
              </v-row>
              
              
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog
          overlay-color=var(--primary-blue)
          overlay-opacity="0.9"
          v-model="cacheDialog"
          persistent
          max-width="290"
        >        
          <v-card class="intro-dark">
            <v-card-title>
              <mrx-translation token="app.device.list.cache_refresh.dialog.title"></mrx-translation>
            </v-card-title>
            <v-card-text class="modal-text">
              <mrx-translation token="app.device.list.cache_refresh.dialog.message"></mrx-translation>
            </v-card-text>
            <v-card-actions class="px-5 pb-5 pt-0 justify-stretch align-stretch">
              <v-spacer></v-spacer>
              <v-row no-gutters class="flex xs6 pe-2">
                <v-btn 
                  id="cancel-refresh-cache" 
                  class="cancel-btn" 
                  outlined 
                  block 
                  depressed 
                  @click="cacheDialog=false">
                  <mrx-translation token="app.device.list.cache_refresh.dialog.cancel"></mrx-translation>
                </v-btn>
              </v-row>
              <v-row no-gutters class="flex xs6 ps-2">
                <v-btn 
                  id="confirm-refresh-cache" 
                  class="modal-btn" 
                  block 
                  depressed 
                  @click="refreshCache()">
                  <mrx-translation token="app.device.list.cache_refresh.dialog.ok"></mrx-translation>
                </v-btn>
              </v-row>
              
              
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>
  </v-layout>
</template>

<script>
  function timeSince(date) {
    var seconds = Math.floor((new Date() - new Date(date)) / 1000);

    var interval = seconds / 31536000;

    if (interval > 1) {
      return { count: Math.floor(interval), label: 'years' }
    }
    interval = seconds / 2592000;
    if (interval > 1) {
      return { count: Math.floor(interval), label: 'months' }
    }
    interval = seconds / 86400;
    if (interval > 1) {
      return { count: Math.floor(interval), label: 'days' }
    }
    interval = seconds / 3600;
    if (interval > 1) {
      return { count: Math.floor(interval), label: 'hours' }
    }
    interval = seconds / 60;
    if (interval > 1) {
      return { count: Math.floor(interval), label: 'minutes' }
    }
    return { count: Math.floor(seconds), label: 'seconds' }
  }

  import mrx from '@/mixins/mrx'
  import icons from '@/mixins/icons'
  import DiagnosticLogViewer from '@/components/layouts/DiagnosticLogViewer'
  import { mapGetters, mapActions } from 'vuex'

  let queueName = 'workbox-background-sync:thinkmd-collect'

  export default {
    name: 'Device',
    components: { DiagnosticLogViewer },
    mixins: [mrx, icons],
    data() {
      return {
        activeTab: 'status',
        cacheDialog: false,
        resetDialog: false,
        ready: false,
        syncActive: false,
        geoPermissionText: 'responses.geo_permission.message.searching.title',
        geoPermissionStatus: '',
        offlineText: 'app.device.list.offline.verifying',
        geoPermissionIcon: {},
        offlineIcon: {},
        missing: [],
        interval: null
      }
    },
    destroyed() {
      clearInterval(this.interval)
    },
    computed: {

      timeSinceLastUpload() {
        return this.lastUpload ? timeSince(this.lastUpload) : { count: '', label: 'never' }
      },

      records() {
        return this.$store.state.recordsInQueue
      },

      mbps() {
        return window
          && window.navigator
          && window.navigator.connection
          && window.navigator.connection.downlink
          || 0.001
      },

      estimatedTime() {
        let timeInSeconds = (10 / (this.mbps / 8))
        return new Date(timeInSeconds * 1000).toISOString().substr(11, 8)
      },

      queueExists() {
        let ret = false
        try {
          ret = !window.indexedDB
        }
        catch (e) {

        }
        return ret
      },

      offlineEnabled() {
        return this.$store.state.settings.offline
      },

      queueStartedAt() {
        return this.$store.state.v4.collect.queueStartedAt
      },

      connection() {
        return this.serverIsAvailable
      },

      online() {
        return this.$online
      },

      connectionStatusText() {
        let res = 'app.device.list.connection_status.unknown'
        try {
          res = this.connection ? 'app.device.list.connection_status.online' : 'app.device.list.connection_status.offline'
        }
        catch (e) {}
        return res
      },

      cacheRefreshText() {
        return this.online ? 'app.device.list.cache_refresh.online' : 'app.device.list.cache_refresh.offline'
      },

      recordsInQueueIcon() {
        return this.records === 0 ? { color: 'success', icon: this.icons.mdiCheck } : { color: 'orange', icon: this.icons.mdiSwapVertical }
      },

      geoLocationCheckText() {
        return this.geoAcquired ? 'app.device.list.location_check.ok' : 'app.device.list.location_check.error'
      },

      versionCheckText() {
        let status = this.$store.state.v4.healthcheck.status
        return `app.device.list.version_check.${status}`
      },

      versionCheck() {
        return this.$store.state.v4.healthcheck.status === 'up_to_date'
      },

      offlineCheck() {
        return this.$store.state.v4.healthcheck.readyForOffline
      },

      queueCheckIcon() {
        return 'success'
      },

      showUploadButton() {
        return this.$store.getters['v4/mode/get']('showUploadButton')
      },

      connectionStatusIcon() {
        return `${this.sharedPath}connection_status.png`
      },

      ...mapGetters({
        geoAcquired: 'v4/geo/acquired',
        totalCompletedTracks: 'v4/collect/getTotalCompletedTracks',
        totalUploads: 'v4/collect/getTotalUploads',
        lastUpload: 'v4/collect/lastUpload',
        serverIsAvailable: 'v4/collect/serverIsAvailable',
        sharedPath: 'v4/sharedPath', settings: 'v4/settings/get',
        getMode: 'v4/mode/get',
      })

    },
    methods: {
      resetQueueData() {
        this.resetDialog = false
        this.resetQueue()
      },
      badgeColor(property) {
        return property ? 'success' : 'caution'
      },
      async syncQueuedEncounters() {
        this.syncActive = true;

        let sw = await navigator.serviceWorker.ready;

        await this.$store.dispatch('v4/collect/genericEvent', {
          name: 'uploadRecordsButtonClick',
          additions: ['globalSequence', 'encountersInQueue'],
        });

        try {
          sw.sync.register(queueName);
        } catch (e) {
          console.log(e);
          this.$sentry.captureException(e);
          this.syncFallback();
        }

        if (!this.interval) this.startInterval();
      },
      syncFallback() {
        try {
          let channel = new BroadcastChannel('sw-messages');
          channel.postMessage('SYNC_RECORDS');
        } catch (e) {
          console.log(e);
          this.$sentry.captureException(e);
        }
      },
      async syncEventActive() {
        try {
          let sw = await navigator.serviceWorker.ready;
          let tags = await sw.sync.getTags();
          let records = await this.recordsInQueue();
          
          this.syncActive = tags.includes(queueName) && records > 0;
        } catch (e) {
          console.error('Error checking sync event status:', e);
        }
      },
      async geoPermissionMessage() {
        let panel = await this.$store.getters['v4/preference/get']('geo_permission')
        let message = panel.responses.find(x => x.name === 'geo_permission.message')
        this.geoPermissionStatus = message.value || 'error'
        this.geoPermissionText = `responses.geo_permission.message.${message}.title`
      },
      async healthCheck() {
        await this.$store.dispatch('v4/healthcheck/send')
      },
      startInterval() {
        this.interval = setInterval(async() => {
          let val = await this.checkIfServerIsAvailable()
          this.syncEventActive()
          if (val) {
            clearInterval(this.interval)
            this.interval = null
          }
        }, 2000)
      },
      async refreshCache() {
        this.cacheDialog = false
        try {
          await this.$store.dispatch('v4/cacheRefresh/send');
        } catch (e) {
          console.log(e);
          this.$sentry.captureException(e);
        }
      },
      ...mapActions({
        previous: 'v4/navigate/previous',
        checkIfServerIsAvailable: 'v4/collect/checkIfServerIsAvailable',
        resetQueue: 'v4/collect/resetQueue',
      })
    },

    watch: {
      online: function(val) {
        if (!val && !this.interval) this.startInterval()
      },
      connection: function(val) {
        if (!val && !this.interval) this.startInterval()
      }
    },

    async mounted() {
      this.syncEventActive()
      this.geoPermissionIcon = { color: 'warning', icon: this.icons.mdiMagnify }
      this.offlineIcon = { color: 'warning', icon: this.icons.mdiSearch }
      this.geoPermissionMessage()
      this.recordsInQueue()
      this.healthCheck()
      this.startInterval()
    }
  }
</script>

<style scoped>
  h2 {
    font-weight: 600;
    font-size: 18px;
  }

  .device-tabs-wrapper {
    display: flex;
    justify-content: center;
    padding: 0 24px;
    margin-bottom: 4px;
  }

  .device-tabs {
    display: flex;
    gap: 0;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 4px;
    width: 100%;
    max-width: 320px;
  }

  .device-tab {
    flex: 1;
    padding: 10px 24px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 1.2px;
    color: rgba(255, 255, 255, 0.45);
    background: transparent;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.25s ease;
    outline: none;
    text-transform: uppercase;
  }

  .device-tab:hover {
    color: rgba(255, 255, 255, 0.7);
    background: rgba(255, 255, 255, 0.05);
  }

  .device-tab--active {
    color: #00e676;
    background: rgba(0, 230, 118, 0.08);
    box-shadow: 0 0 12px rgba(0, 230, 118, 0.1);
  }

  .device-tab--active:hover {
    color: #00e676;
    background: rgba(0, 230, 118, 0.12);
  }

  #device {
    display: flex;
    flex-direction: column;
    height: 800px;
    max-height: 82vh;
    width: calc(100% - 32px) !important;
    max-width: 480px !important;
    min-width: 0 !important;
    margin: 26px auto !important;
    box-sizing: border-box !important;
    transition: none !important;
  }

  @media (min-width: 500px) {
    #device {
      width: 480px !important;
      min-width: 480px !important;
      max-width: 480px !important;
    }
  }
  
  .device-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .status-tab-wrapper {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  
  .status-tab-wrapper::-webkit-scrollbar {
    display: none;
  }

  .logs-tab-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
  }

  .logs-viewer-flex {
    flex: 1;
    min-height: 0;
  }
</style>